import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./style.module.css";
import clsx from "clsx";
import { useBlog } from "api/blog";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { convertToSnakeCase } from "utils/blog";
import { Affix, AffixProps, Col, Row, Space } from "antd";
import SectionList from "./components/SectionList";
import CategoryList from "./components/CategoryList";
import { Breadcrumb } from "antd";
import { routesEnum } from "pages/Routes";
import { getImageUrl } from "utils/urlParser";
import { useEffect } from "react";

const generateSections = (content: string) => {
  const div = document.createElement("div");
  div.innerHTML = content;
  const headings = ["H1", "H2", "H3", "H4", "H5", "H6"];
  const children = div.children;
  Array.from(children).forEach((child, index) => {
    if (headings.includes(child.tagName))
      child.id = `${convertToSnakeCase(child.textContent ?? "")}_${index}`;
  });
  return div.innerHTML;
};

const BlogDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { data, isLoading } = useBlog({ id }, { enabled: !!id });
  const menuRef = useRef<any>(null);
  const [affixProps, setAffixProps] = useState<AffixProps>({
    offsetTop: 30,
    children: <></>,
  });

  const blogContent = useMemo(() => {
    if (data) {
      return generateSections(data.content);
    }
    return "";
  }, [data]);

  useLayoutEffect(() => {
    if (data) {
      if (location.hash) {
        const element = document.getElementById(location.hash.replace("#", ""));
        if (element) {
          const bodyRect = document.body.getBoundingClientRect();
          const elemRect = element.getBoundingClientRect();
          const offset = elemRect.top - bodyRect.top;
          window.scrollTo(0, offset - 150);
        }
      } else {
        window.scrollTo(0, 0);
      }
    }
  }, [data, location.hash]);

  useEffect(() => {
    const listener = () => {
      if (menuRef.current) {
        const menu: HTMLDivElement = menuRef.current.placeholderNode;
        const footer = document.getElementById("footer");
        const heightOffsetMenu =
          (window.pageYOffset || window.scrollY) + menu.clientHeight + 80;
        const offsetTop = (footer?.offsetTop || 0) - heightOffsetMenu;

        if (offsetTop < 0)
          setAffixProps((prev) => ({
            ...prev,
            offsetTop: offsetTop,
          }));
        else if (offsetTop > 0 && affixProps.offsetTop! < 0)
          setAffixProps((prev) => ({
            ...prev,
            offsetTop: 30,
          }));
      }
    };

    listener();

    window.addEventListener("resize", listener);
    window.addEventListener("scroll", listener);

    return () => {
      window.removeEventListener("resize", listener);
      window.removeEventListener("scroll", listener);
    };
  }, [menuRef.current, affixProps.offsetTop]);

  if (isLoading || !data) {
    return <></>;
  }

  return (
    <>
      <div className={styles.bread_crumb_box}>
        <Breadcrumb separator=">">
          <Breadcrumb.Item href={routesEnum.home}>Home</Breadcrumb.Item>
          <Breadcrumb.Item href={routesEnum.blog}>Blog</Breadcrumb.Item>
          <Breadcrumb.Item href={`${routesEnum.blog}/${id}`}>
            {data.title}
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <div className={clsx(styles.root)}>
        <Row gutter={[48, 48]} style={{ overflow: "hidden" }}>
          <Col span={18}>
            <h1 className={styles.title}>{data.title}</h1>
            <span>{moment(data.updatedAt).format("dddd, MMMM D, YYYY")}</span>
            <img
              className={styles.cover_image}
              src={getImageUrl(data.coverImage)}
              alt={convertToSnakeCase(data.title)}
            />
            <div
              id="blog-content"
              dangerouslySetInnerHTML={{ __html: blogContent }}
              style={{ marginBottom: "120px" }}
            />
          </Col>
          <Col span={6}>
            <Affix {...affixProps} ref={menuRef}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <SectionList />
                <CategoryList />
              </Space>
            </Affix>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default BlogDetail;
